import React, { useEffect, useMemo, useState } from 'react';
import { Order } from 'shared/types/cart';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { stripVariantSuffix } from '@/components/customercenter-ui/utils/formatUtils';
import { useCart } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { createRecommendClient } from '@/helpers/algolia/algoliaRecommendV5';
import { fetchAlgoliaSourceProductForRecommend } from '@/helpers/utils/fetchAlgoliaSourceProductForRecommend';
import { getRecommendAlgoliaIndexName } from '@/helpers/utils/recommendAlgoliaIndexName';
import { routerUrl } from '@/helpers/utils/routerUrl';
import {
  buildSimilarItemsQueryParameters,
  getSourceProductAttributesToRetrieve,
  resolveStudioAttributeValues,
} from '@/helpers/utils/similarItemsQueryParameters';
import AlgoliaRecommendProductsList from './algoliaRecommendProductsList';

const RELATED_ITEMS_BASE_QUERY_PARAMETERS = {
  attributesToRetrieve: ['*'] as string[],
  enableABTest: false as const,
  enableRules: true as const,
  getRankingInfo: false as const,
  enablePersonalization: true as const,
};

interface RelatedItemsProps {
  siteId?: string;
  maxRelatedProductsPerPage?: number;
  objectId?: any;
  setProductsStatus: (haveProducts: boolean) => void;
  data: any;
  setProductsHave: (haveProducts: boolean) => void;
}

const RelatedItems: React.FC<RelatedItemsProps> = ({
  siteId,
  maxRelatedProductsPerPage,
  setProductsStatus,
  data,
  setProductsHave,
}) => {
  const { data: cart } = useCart(false);
  const projectSettings: ProjectSettings = useProjSettings();
  const [relatedItems, setRelatedItems] = useState<any>(undefined);
  const objectID = data?.data?.data?.dataSource?.product?.productId ?? data?.data?.nonPdpProductId;
  const masterVariantAttributes = data?.data?.data?.dataSource?.product?.masterVariantAttributes ?? {};
  const attributesToFilter = projectSettings?.pdpRelatedItemsAttributesToFilter ?? [];
  const pageType = data?.data?.productListCarousel?.dataSource?.pageType;
  const masterVariantAttributesKey = JSON.stringify(
    data?.data?.data?.dataSource?.product?.masterVariantAttributes ?? null,
  );

  const isPdpPage = useMemo(() => {
    const normalizedPageType = `/${pageType}`;
    return normalizedPageType !== routerUrl.cart && normalizedPageType !== routerUrl.thankYou;
  }, [pageType]);

  const objectIDs: string[] = useMemo(() => {
    const normalizedPageType = `/${pageType}`;

    if (normalizedPageType === routerUrl.cart && cart?.lineItems) {
      return cart.lineItems
        .map((lineItem) => lineItem.productKey)
        .filter((productKey): productKey is string => productKey !== undefined);
    }

    if (normalizedPageType === routerUrl.thankYou) {
      const order = data?.data?.productListCarousel?.dataSource?.order as Order;
      if (order?.lineItems) {
        return order.lineItems
          .map((lineItem) => lineItem.productKey)
          .filter((productKey): productKey is string => productKey !== undefined);
      }
    }

    return objectID ? [objectID] : [];
  }, [pageType, cart?.lineItems, objectID, data]);

  useEffect(() => {
    if (!siteId || !projectSettings?.algoliaAppId || !projectSettings?.algoliaApiKey || objectIDs.length === 0) {
      setProductsHave(false);
      setProductsStatus(false);
      return;
    }

    const recommendClient = createRecommendClient(projectSettings.algoliaAppId, projectSettings.algoliaApiKey);
    if (!recommendClient) {
      return;
    }

    let isCancelled = false;
    const indexName = getRecommendAlgoliaIndexName(siteId, projectSettings);

    const applyRecommendResults = (recommendations: any) => {
      if (isCancelled || !recommendations?.results) {
        return;
      }

      const relatedItems: any[] = [];
      recommendations.results
        ?.filter((item: any, index: any) => recommendations.results.indexOf(item) === index)
        ?.forEach((item: any) => {
          if (item?.hits && item.hits.length > 0) {
            relatedItems.push(
              ...item.hits.map((hit: any, idx: number) => ({
                ...hit,
                ['__queryID']: item?.queryID,
                ['__position']: idx + 1,
              })),
            );
          }
        });
      setRelatedItems(relatedItems);
      const hasProducts = relatedItems.length > 0;
      setProductsHave(hasProducts);
      setProductsStatus(hasProducts);
    };

    const handleRecommendError = (error: unknown) => {
      if (isCancelled) {
        return;
      }
      console.error('Error while fetching data:', error);
      setProductsHave(false);
      setProductsStatus(false);
      setRelatedItems(undefined);
    };

    if (!isPdpPage) {
      const recommendationQueries = objectIDs.map((id) => ({
        queryParameters: {
          ...RELATED_ITEMS_BASE_QUERY_PARAMETERS,
          filters: 'isInStock:true',
          clickAnalytics: true,
        },
        maxRecommendations: maxRelatedProductsPerPage ?? 15,
        model: 'related-products' as const,
        indexName,
        objectID: stripVariantSuffix(id),
        threshold: 0,
      }));

      recommendClient
        .getRecommendations({
          requests: recommendationQueries.map((query: any) => ({
            ...query,
            queryParameters: {
              ...query.queryParameters,
              clickAnalytics: true,
            },
          })),
        })
        .then(applyRecommendResults)
        .catch(handleRecommendError);

      return () => {
        isCancelled = true;
      };
    }

    const fetchPdpRelatedItems = async () => {
      try {
        const recommendationQueries = await Promise.all(
          objectIDs.map(async (id) => {
            const normalizedObjectId = stripVariantSuffix(id);
            const sourceProduct = await fetchAlgoliaSourceProductForRecommend({
              appId: projectSettings.algoliaAppId,
              apiKey: projectSettings.algoliaApiKey,
              indexName,
              objectId: normalizedObjectId,
              attributesToRetrieve: getSourceProductAttributesToRetrieve(attributesToFilter),
            });

            const attributeValues = resolveStudioAttributeValues(
              attributesToFilter,
              sourceProduct ?? {},
              masterVariantAttributes,
            );

            const relatedItemsQueryParameters = buildSimilarItemsQueryParameters({
              hierarchicalCategories: sourceProduct?.hierarchicalCategories,
              categories: sourceProduct?.categories,
              attributesToFilter,
              attributeValues,
            });

            return {
              queryParameters: {
                ...RELATED_ITEMS_BASE_QUERY_PARAMETERS,
                ...relatedItemsQueryParameters,
                clickAnalytics: true,
              },
              maxRecommendations: maxRelatedProductsPerPage ?? 15,
              model: 'related-products' as const,
              indexName,
              objectID: normalizedObjectId,
              threshold: 0,
            };
          }),
        );

        if (isCancelled) {
          return;
        }

        const recommendations = await recommendClient.getRecommendations({
          requests: recommendationQueries.map((query: any) => ({
            ...query,
            queryParameters: {
              ...query.queryParameters,
              clickAnalytics: true,
            },
          })),
        });

        applyRecommendResults(recommendations);
      } catch (error) {
        handleRecommendError(error);
      }
    };

    fetchPdpRelatedItems();

    return () => {
      isCancelled = true;
    };
  }, [
    objectIDs,
    projectSettings?.algoliaAppId,
    projectSettings?.algoliaApiKey,
    siteId,
    maxRelatedProductsPerPage,
    isPdpPage,
    projectSettings?.pdpRelatedItemsAttributesToFilter,
    masterVariantAttributesKey,
  ]);

  return <section id="relatedItems">{relatedItems && <AlgoliaRecommendProductsList item={relatedItems} />}</section>;
};

export default RelatedItems;
